import { BaseAdapter } from "@modules/+new/payments/shared-payments/entities/base-adapter";
import { GetStakingOverviewQuery, StakingType } from "@gql/staking";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { AccountService } from "@modules/account/services/account.service";
import { Tradable } from "@models/interfaces/tradable";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import {
	StakingOverviewItem,
	StakingOverviewState,
	StakingRewardAmountModel
} from "@modules/staking/domain/entities/staking-overview-state.model";
import { PortfolioAssetDetails } from "@modules/portfolio/domain/entities/portfolio-overview.model";

export class StakingOverviewStateAdapter extends BaseAdapter<GetStakingOverviewQuery, StakingOverviewState> {
	public constructor(
		private readonly tradableListService: TradableListService,
		private readonly accountService: AccountService
	) {
		super();
	}

	public adapt(item: GetStakingOverviewQuery, ownedAssets?: PortfolioAssetDetails[]): StakingOverviewState {
		const rewards: StakingRewardAmountModel[] = item.lifetimeRewards.map((item) => ({
			assetId: item.asset.assetId,
			assetAmount: item.rewardsAmount.asset.value
		}));
		const stakedAmounts: StakingRewardAmountModel[] = item.balances.map((item) => ({
			assetId: item.asset.assetId,
			assetAmount: item.stakedAmount.value
		}));
		// Enabled Stakeables
		const enabledStakeables = item?.assetConfig?.assets.filter(
			(stakeableItem) => stakeableItem.availability === "ENABLED" && stakeableItem.isAutoStakingEnabled
		);
		// Owned Stakeables
		const ownedStakeables = enabledStakeables.filter((stakeableItem) =>
			ownedAssets.some((portfolioItem) => portfolioItem.asset_id === stakeableItem.assetId)
		);

		// Get the highest yield from the auto staking assets
		const highestYield = ownedStakeables.reduce(
			(max, stakeable) => (stakeable.yearlyInterest > max.yearlyInterest ? stakeable : max),
			item.assetConfig.assets[0]
		).yearlyInterest;

		// Assets for auto staking confirmation
		const mappedAutoStakeAssets = ownedStakeables.map((asset) => ({
			assetId: asset.assetId,
			assetSymbol: this.tradableListService.getItemById(asset.assetId)?.getSymbol() || "",
			logo: this.tradableListService.getItemById(asset.assetId)?.getLogo() || "",
			yield: asset.yearlyInterest,
			unBoundPeriod: asset.unboundPeriod ?? 0,
			availability: asset.availability
		}));

		return {
			init: true,
			hasError: false,
			nonStakedAssetsCount: item.statistics.nonStakedAssetsCount,
			totalEarnedRewards: this.getTotalAmount(rewards),
			totalStakedAmount: this.getTotalAmount(stakedAmounts),
			overviewItems: this.getSortedOverviewItems(item),
			highestOwnedApr: highestYield ?? 0,
			autoStakeAssets: mappedAutoStakeAssets ?? [],
			hasHistory: item.hasHistory
		};
	}

	private getSortedOverviewItems(info: GetStakingOverviewQuery): StakingOverviewItem[] {
		return [...this.parseOverviewItems(info)].sort(function (a, b) {
			return +b.lifetimeAssetRewards - +a.lifetimeAssetRewards;
		});
	}

	private parseOverviewItems(info: GetStakingOverviewQuery): StakingOverviewItem[] {
		return info.balances.map((item) => {
			const overviewItem: StakingOverviewItem = {
				assetId: item.asset.assetId,
				assetName: this.tradableListService.getItemById(item.asset.assetId)?.getName() || "",
				stakedAmount: item.stakedAmount.value,
				lifetimeAssetRewards:
					info.lifetimeRewards.find((reward) => reward.asset.assetId === item.asset.assetId)?.rewardsAmount
						.asset.value || "0",
				isLocked: item.asset.stakingType === StakingType.Locked,
				yearlyInterest: info.assetConfig.assets.find((asset) => asset.assetId === item.asset.assetId)
					?.yearlyInterest
			};
			return overviewItem;
		});
	}

	private getTotalAmount(amounts: StakingRewardAmountModel[]): number {
		return amounts
			.map((item) => {
				const tradable: Tradable = this.tradableListService.getItemById(item.assetId);
				return +convertTradableToFiat(tradable, +item.assetAmount, this.accountService.getDefaultFiat());
			})
			.reduce((sum: number, balance: number) => sum + balance, 0);
	}
}
